import Container from '@brands/Components/Container/Container';
import UnsavedChangesModal from '@brands/Dashboard/Dashboard/Components/UnsavedChangesModal/UnsavedChangesModal';
import { useWindowSize } from '@brands/hooks';
import { useUnsavedChangesWarning } from '@brands/hooks/useUnsavedChangesWarning';
import { getCurrentProfile } from '@brands/services/identity/getCurrentProfile';
import { getUserById } from '@brands/services/identity/getUserById';
import { listAllLanguages } from '@brands/services/identity/listAllLanguages';
import { listInsuranceCarriers } from '@brands/services/identity/listInsuranceCarriers';
import { mapProfileToMyProfileSubmit } from '@brands/store/actions/providerAction';
import { setProviderMyProfile } from '@brands/store/slices/providerMyProfileSlice';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MultiValue } from 'react-select';

import { Loading } from '../../../Components/LoadingSpinner/Loading';
import { useAppDispatch } from '../../../hooks/useReduxHook';
import { defaultUserValues } from '../../../services/cases/types/defaultUserValues';
import { Language, License, UserProfile } from '../../../services/identity/types/UserProfile';
import { displayErrorDetails } from '../../../Utils/displayError';
import { OptionInsurance, OptionLanguage } from '../../../Utils/selectOptions';
import ChangeUserStatusModal from '../Modal/ChangeUserStatusModal';
import ResetPasswordModal from '../Modal/ResetPasswordModal';
import styles from '../styles.module.scss';
import Profile from './Profile';
import { ProviderMyProfileSubmit } from './utils/types';

type ReviewMyProfileProps = {
  reviewProfile?: boolean;
};

const MyProfile = ({ reviewProfile = false }: ReviewMyProfileProps): JSX.Element => {
  const { userId } = useParams();
  const dispatch = useAppDispatch();
  const screenSize = useWindowSize();
  const formRef = useRef<HTMLFormElement>(null);

  const [loadingUser, setloadingUser] = useState(true);
  const [currentAvatarURL, setCurrentAvatarURL] = useState<string>('');

  const [thisUser, setThisUser] = useState<UserProfile>(defaultUserValues);
  const [languageOptions, setLanguageOptions] = useState<OptionLanguage[]>([]);
  const [insurancePlanName, setInsurancePlanName] = useState<OptionInsurance[]>([]);
  const [myProfileForm, setMyProfileForm] = useState<ProviderMyProfileSubmit | null>(null);

  const [userLoaded, setUserLoaded] = useState(false);
  const [languagesLoaded, setLanguagesLoaded] = useState(false);

  const [isDirty, setIsDirty] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [resetPasswordConfirmation, setResetPasswordConfirmation] = useState<{
    isOpen: boolean;
    userState: string;
  }>({ isOpen: false, userState: '' });
  const [changeUserStatusFlag, setChangeUserStatusFlag] = useState('');

  const { showModal, confirmNavigation, saveChanges, hideModal } = useUnsavedChangesWarning(isDirty, formRef);

  useEffect(() => {
    const fetchStaticData = async () => {
      try {
        setloadingUser(true);
        const [languages, insurance] = await Promise.all([listAllLanguages(), listInsuranceCarriers()]);

        setLanguageOptions(
          languages.map((l) => ({
            id: l.id,
            value: l.locale,
            label: l.name,
          })),
        );

        setInsurancePlanName(
          insurance.map((i) => ({
            id: i.id,
            value: i.id,
            label: i.name,
          })),
        );

        setLanguagesLoaded(true);
      } catch (error) {
        displayErrorDetails(error);
      } finally {
        setloadingUser(false);
      }
    };

    fetchStaticData();
  }, [reviewProfile]);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        setloadingUser(true);
        const user = userId
          ? reviewProfile
            ? await getUserById(Number(userId))
            : await getCurrentProfile()
          : await getCurrentProfile();

        setThisUser(user as UserProfile);
        setUserLoaded(true);
      } catch (error) {
        displayErrorDetails(error);
      } finally {
        setloadingUser(false);
      }
    };

    fetchUser();
  }, [userId, reviewProfile]);

  useEffect(() => {
    if (!userLoaded || !languagesLoaded) return;

    const mappedProfile = mapProfileToMyProfileSubmit(thisUser);

    const selectedLanguages: MultiValue<OptionLanguage> = languageOptions.filter((lang) =>
      mappedProfile.languages?.some((l) => Number(l?.id) === Number(lang.id)),
    );

    const licences: License[] =
      thisUser.provider_profile?.licenses?.map((licence) => ({
        id: licence.id ?? 0,
        state: licence.state ?? '',
        number: licence.number ?? '',
        expiry_date: moment(licence.expiry_date).format('MM/DD/YYYY'),
        insurance_carriers: licence.insurance_carriers.map((carrier) => ({
          insurance_carrier_id: carrier.insurance_carrier_id ?? carrier.insurance_carrier?.id,
          insurance_carrier: carrier.insurance_carrier,
          effective_date: moment(carrier.effective_date).format('MM/DD/YYYY'),
          recredential_date: moment(carrier.recredential_date).format('MM/DD/YYYY'),
        })),
      })) ?? [];

    const formData: ProviderMyProfileSubmit = {
      ...mappedProfile,
      language: selectedLanguages,
      profile_img: currentAvatarURL || thisUser.picture_media_id || '',
      licences,
      client_divisions: thisUser.divisions || [],
      network: thisUser.network ?? null,
    };

    setMyProfileForm(formData);
    dispatch(setProviderMyProfile(formData));
    setloadingUser(false);
  }, [userLoaded, languagesLoaded]);

  useEffect(() => {
    if (reviewProfile) {
      setResetPasswordConfirmation((prev) => ({ ...prev, userState: thisUser.state || '' }));
    }
  }, [reviewProfile, thisUser.state]);

  const disableResetButton = (): void => {
    setResetEmailSent(true);
  };

  const disableDeleteButton = (): void => {
    setThisUser((prev) => ({ ...prev, status: 'Deactivated' }));
  };

  if (loadingUser || !myProfileForm?.providerID) {
    return <Loading fullScreen />;
  }

  const profileContent = (
    <div className={styles.myProfileContainer}>
      <Profile
        isProfileCard
        formRef={formRef}
        setIsDirty={setIsDirty}
        myProfileForm={myProfileForm}
        setMyProfileForm={setMyProfileForm}
        insurancePlanName={insurancePlanName}
        thisUser={thisUser}
        setThisUser={setThisUser}
        setCurrentAvatarURL={setCurrentAvatarURL}
        setUserLoaded={setUserLoaded}
        userLoaded={userLoaded}
        reviewProfile={reviewProfile}
        loadingUser={loadingUser}
        resetEmailSent={resetEmailSent}
        setResetPasswordConfirmation={setResetPasswordConfirmation}
        setChangeUserStatusFlag={setChangeUserStatusFlag}
        userId={userId}
      />

      {showModal && (
        <UnsavedChangesModal
          isOpen={showModal}
          onConfirm={confirmNavigation}
          saveChange={saveChanges}
          loading={false}
          hideModal={hideModal}
        />
      )}
      {reviewProfile && resetPasswordConfirmation.isOpen && userId && (
        <ResetPasswordModal
          setOpenModal={setResetPasswordConfirmation}
          disableButton={disableResetButton}
          userId={Number(userId)}
          userEmail={thisUser.email}
          userName={thisUser.full_name}
          isDeactivated={thisUser.status === 'Deactivated'}
          userState={resetPasswordConfirmation.userState}
        />
      )}
      {reviewProfile && changeUserStatusFlag && userId && (
        <ChangeUserStatusModal
          setOpenModal={setChangeUserStatusFlag}
          disableButton={disableDeleteButton}
          userId={Number(userId)}
          userName={thisUser.full_name}
          status={changeUserStatusFlag}
          thisUser={thisUser}
          setThisUser={setThisUser}
        />
      )}
    </div>
  );

  if (reviewProfile) {
    return profileContent;
  }

  return (
    <Container
      patientDashboard
      childClassName={styles.dashboard}
      isSidenavVisible
      containerStyle={{
        justifyContent: screenSize.width >= 1289 ? 'center' : 'start',
        overflowY: screenSize.width >= 1289 ? 'hidden' : 'scroll',
      }}
    >
      {profileContent}
    </Container>
  );
};

export default MyProfile;
